import { Materials, PreProcessingTypes, BuildingSource, GeoObjectMeshOptions } from '@visualizer/types';
import * as THREE from 'three';
import { GeoObjectBatched } from '@data/geodata/GeoObjectBatched';
import type { Intersection } from 'three';
import type { HighlightVisual } from '../edit/Interfaces/HighlightVisual';
import type { ObjectVisual } from '../edit/Interfaces/ObjectVisual';

type TreeTypes = [PreProcessingTypes.TREES];

interface BatchedIntersection extends Intersection {
    batchId?: number;
}

function getInstanceIdFromIntersection(i: BatchedIntersection): number | null {
    if (typeof i.batchId === 'number') return i.batchId;
    if (typeof i.instanceId === 'number') return i.instanceId;
    return null;
}

export class TreesBatched extends GeoObjectBatched<[PreProcessingTypes.TREES]> implements ObjectVisual, HighlightVisual {
    // treeId -> instanceId and reverse
    private readonly treeIdToInstance = new Map<number, number>();
    private readonly instanceToTreeId = new Map<number, number>();

    private readonly storedMatrices = new Map<number, THREE.Matrix4>();
    private readonly highlightedInstances = new Set<number>();

    constructor({ name, colors, data, mode }: Omit<GeoObjectMeshOptions<TreeTypes>, 'types' | 'thresholdAngle'>) {
        super({
            name,
            colors,
            mode,
            types: [PreProcessingTypes.TREES],
            data,
        });

        this.userData.treeSource = BuildingSource.SWISSTOPO;

        // Build mapping
        const group = data.find((g) => g.type === PreProcessingTypes.TREES);
        if (group) {
            group.objects.forEach((obj, instanceId) => {
                const triWithId = obj.triangles.find((t) => t.ID != null);
                const treeId = triWithId?.ID;
                if (treeId == null) return;

                this.treeIdToInstance.set(treeId, instanceId);
                this.instanceToTreeId.set(instanceId, treeId);
            });
        }

        // mark as raycastable target
        this.objects[PreProcessingTypes.TREES].meshes.userData.treeGeometry = true;
    }

    protected override createMaterials(): Materials<[PreProcessingTypes.TREES]> {
        return {
            trees: {
                material: new THREE.MeshBasicMaterial({
                    color: 0x0,
                    side: THREE.DoubleSide,
                    polygonOffset: true,
                    depthTest: true,
                    polygonOffsetFactor: 3,
                    polygonOffsetUnits: 3,
                }),
                shadowMaterial: new THREE.MeshLambertMaterial({ color: 0x0, side: THREE.DoubleSide }),
                stroke: new THREE.LineBasicMaterial({
                    color: 0x0,
                    linewidth: 1,
                }),
            },
        };
    }

    // ---- picking helper ----
    getTreeIdFromIntersection(intersection: Intersection): number | null {
        const instanceId = getInstanceIdFromIntersection(intersection as BatchedIntersection);
        if (instanceId == null) return null;
        return this.instanceToTreeId.get(instanceId) ?? null;
    }

    // ---- ObjectVisual ----
    hideObject(treeId: number): void {
        const instanceId = this.treeIdToInstance.get(treeId);
        if (instanceId == null) return;

        const type = PreProcessingTypes.TREES;
        const mesh = this.objects[type].meshes;

        const tmp = new THREE.Matrix4();
        const pos = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scale = new THREE.Vector3();

        if (!this.storedMatrices.has(instanceId)) {
            mesh.getMatrixAt(instanceId, tmp);
            this.storedMatrices.set(instanceId, tmp.clone());
        } else {
            tmp.copy(this.storedMatrices.get(instanceId)!);
        }

        tmp.decompose(pos, quat, scale);
        scale.set(0, 0, 0);
        tmp.compose(pos, quat, scale);

        mesh.setMatrixAt(instanceId, tmp);
    }

    showObject(treeId: number): void {
        const instanceId = this.treeIdToInstance.get(treeId);
        if (instanceId == null) return;

        const original = this.storedMatrices.get(instanceId);
        if (!original) return;

        const type = PreProcessingTypes.TREES;
        const mesh = this.objects[type].meshes;

        mesh.setMatrixAt(instanceId, original);
        this.storedMatrices.delete(instanceId);
    }

    // ---- HighlightVisual (color only, like your buildings now) ----
    highlightObject(treeId: number): void {
        const instanceId = this.treeIdToInstance.get(treeId);
        if (instanceId == null) return;

        const type = PreProcessingTypes.TREES;
        const mesh = this.objects[type].meshes as THREE.BatchedMesh;

        mesh.setColorAt(instanceId, new THREE.Color(0xff4444));
        this.highlightedInstances.add(instanceId);
    }

    clearHighlight(): void {
        if (this.highlightedInstances.size === 0) return;

        const type = PreProcessingTypes.TREES;
        const mesh = this.objects[type].meshes as THREE.BatchedMesh;
        const white = new THREE.Color(0xffffff);

        for (const instanceId of this.highlightedInstances) {
            mesh.setColorAt(instanceId, white);
        }
        this.highlightedInstances.clear();
    }
}
